/**
 * User Notices API Types
 * Based on /api/user-notices endpoints
 */

export type NoticePriority = 'low' | 'normal' | 'high' | 'urgent';

// Notice Type GUIDs from API documentation
export const NoticeTypeIds = {
  FRIEND_REQUEST: 'CC289ED3-A2A1-4800-95C7-198E3E86046E',
  FRIEND_REQUEST_ACCEPTED: 'D1B5F8A2-3C4E-4F6A-8B9C-1234567890AB',
  GROUP_INVITATION: '11111111-1111-1111-1111-111111111111',
  GROUP_JOIN_REQUEST: 'A1B2C3D4-E5F6-7890-1234-567890ABCDEF', // GENERAL type used for group join requests
  ADVENTURE_INVITATION: 'B2C3D4E5-F6A7-8901-2345-67890ABCDEF1',
  GENERAL: 'A1B2C3D4-E5F6-7890-1234-567890ABCDEF',
  SYSTEM: 'C3D4E5F6-A7B8-9012-3456-7890ABCDEF12',
  /** Somebody wants you on a trip. Carries Accept / Decline against the share id. */
  TRIP_INVITATION: 'E9F0A1B2-C3D4-5678-9012-CDEF12345678',
  SESSION_INVITE: 'A5B6C7D8-E9F0-1234-5678-90ABCDEF1234',
  MODERATOR_APPROVAL_REQUEST: 'F4A5B6C7-D8E9-0123-4567-890ABCDEF456',
  ADVENTURE_OPENED: 'F0A1B2C3-D4E5-6789-0123-DEF123456789',
  /** Somebody has offered you an adventure. Carries Accept / Decline actions. */
  ADVENTURE_TRANSFER_OFFERED: '1A2B3C4D-5E6F-7081-9203-A4B5C6D7E8F9',
  /**
   * Somebody replied in a discussion you subscribed to. No actions — the row is a link,
   * and the discussion is where the answering happens.
   *
   * Only one of these exists per discussion at a time while it is unread: the server
   * rewrites the outstanding notice in place rather than adding another, so a busy thread
   * costs one row rather than forty. Data is {@link DiscussionReplyData}.
   */
  DISCUSSION_REPLY: '2B3C4D5E-6F70-8192-A3B4-C5D6E7F8091A',
  /**
   * You completed a collection and earned its badge. No actions — Data is the badge id.
   *
   * Until 2026-08-14 this shared TRIP_INVITATION's GUID on the server, so badge notices
   * arrived indistinguishable from trip invitations and were rendered as one. Migration
   * AddDistinctBadgeEarnedNoticeType moved the existing rows onto this id.
   */
  BADGE_EARNED: '3C4D5E6F-7081-9203-B4C5-D6E7F8091A2B',
  /**
   * You arrived somewhere that counts and the server recorded the visit. No actions —
   * Data is the adventure id, bare rather than JSON.
   *
   * The only completion nobody asked for: a check-in adventure completes because the
   * player is standing in it, with no tap to confirm. Without this notice the visit lands
   * in their history unannounced, which reads as nothing having worked.
   */
  ADVENTURE_CHECKED_IN: '4D5E6F70-8192-A3B4-C5D6-E7F8091A2B3C',
  /**
   * A suggestion you sent about somebody else's adventure has been answered.
   *
   * One type for both outcomes rather than two. To the reader they are the same event — the thing
   * I sent has an answer — and every client keeps its own hand-copied list of these GUIDs plus
   * icon, category and link switches, so a second type would double that work across five files
   * for no benefit to anybody reading it. Which way it went is in the title; a refusal carries its
   * reason in the message. Data is the submission id.
   */
  CONTRIBUTION_DECIDED: 'B7D2F1A4-6C3E-4D58-9F21-8AE05C7B3D64',
  /** Somebody suggested a change to an adventure you manage. Data is the adventure id. */
  CONTRIBUTION_RECEIVED: 'FCD40D3F-CC41-4BCF-B0ED-6F9003F40AD0',
  /** A trail route is waiting for review (regional moderators per submission; site moderators daily). Data is the adventure id. */
  TRAIL_ROUTE_SUBMITTED: '8F3C2A61-5D4E-4B7A-9C18-2E6F0B9D4A73',
  /** A moderator approved or rejected the route you submitted; which way is in the title. Data is the adventure id. */
  TRAIL_ROUTE_DECIDED: 'B41E7D95-2C6A-4F03-8E5B-7A19D6C2F048',
  /**
   * Somebody started a discussion in a group you belong to. Data is the discussion id.
   *
   * The group-level counterpart to {@link DISCUSSION_REPLY}: this says a conversation now
   * exists, that one says a conversation you already follow has moved. Being told about it
   * does not sign you up for its replies — that stays a deliberate act, per discussion.
   *
   * Gated by the recipient's group notification settings (the bell on the group page).
   */
  GROUP_DISCUSSION_STARTED: '5E6F7081-92A3-B4C5-D6E7-F8091A2B3C4D',
  /** A group you belong to posted an event. Data is the event id. */
  GROUP_EVENT_POSTED: '6F708192-A3B4-C5D6-E7F8-091A2B3C4D5E',
  /**
   * A group added something to its to-do list, or proposed a date on one. Data is the
   * list item id. One type for both, because they are the same event to a reader: the
   * group is deciding what to do next and wants you in on it.
   */
  GROUP_TODO_ACTIVITY: '708192A3-B4C5-D6E7-F809-1A2B3C4D5E6F',
  /**
   * A group is forming for an adventure you flagged interest in, and you qualify for it.
   * Data is {@link SessionMatchAvailableData}.
   *
   * Only ever sent to people who could actually accept — flagging interest is paid and a lapsed
   * subscriber is skipped — so this never dead-ends at a paywall. It deliberately says nothing
   * about *why* you matched: naming the tag would disclose what the owner filtered on.
   */
  SESSION_MATCH_AVAILABLE: '8A1F0C73-2D64-4B19-9E57-3C0B6D84AF21',
  /**
   * The group you are in settled on a start time — or closed its poll without one. No actions;
   * the row is a link. Data is {@link SessionScheduleDecidedData}.
   *
   * One type for both outcomes, because to a reader they are the same event: the vote I was in
   * has finished. Which way it went is in the title.
   */
  SESSION_SCHEDULE_DECIDED: '5D7E9B02-41AF-4C68-8A3D-7E2C1F06B95D',
  /**
   * Your group chose a different time, and an alternative is available at the one you wanted.
   * Carries Join / No thanks against the proposal id. Data is {@link SessionSplitProposedData}.
   *
   * Nobody is ever auto-enrolled: the alternative is not a real session until somebody accepts,
   * and whoever accepts first owns it. Declining or ignoring it leaves no session behind.
   */
  SESSION_SPLIT_PROPOSED: 'C6B3402E-8F15-4D7A-B09C-52A8E7D31F64',
  /**
   * A group you are in is coming up or changed: a reminder a day before, an hour before and at the
   * planned time; the host moved the time or called it off; you were taken out of it.
   * Data: camelCase JSON `{ sessionId, adventureId, scheduledStartDate, kind }`, kind one of
   * reminder_day | reminder_hour | starting_now | rescheduled | cancelled | removed.
   */
  GROUP_SESSION_UPDATE: '6D1F0E6B-0F59-4C91-AEF1-203301D2E483',
  /**
   * Somebody wants to do a Story with you (a Story party). Accept / Decline carry the party id
   * (`accept_story_party` / `decline_story_party`). Data: camelCase JSON `{ storyId, partyId }`.
   */
  STORY_PARTY_INVITE: '5A7C1E93-2B84-4D6F-A0C5-9E3D71B2F846',
  /** Someone joined or left your Story party, couldn't join, or it ended. Data: `{ storyId, partyId }`. */
  STORY_PARTY_UPDATE: 'C2E94F07-6A1D-4B38-8D52-F17A0E6B39C4',
  /** Your Story party started a stop and you are in it. Data: `{ storyId, partyId, sessionId, adventureId }`. */
  STORY_PARTY_STOP_STARTED: 'E81B5D2C-9F46-4A07-B3E1-6C4D28A91F53',
  /** Your Story party's leader started the drive to a stop. Data: `{ storyId, partyId, adventureId, title, ... }`. */
  STORY_PARTY_DRIVE_STARTED: '3D6F9A1E-7C25-4B80-9E14-A5C2D7F08B36',
} as const;

// Data type identifiers in parsed notice data
export const NoticeDataTypes = {
  GROUP_JOIN_REQUEST: 'GroupJoinRequest',
} as const;

// Backend notice status enum
export enum NoticeStatus {
  New = 0,
  Read = 1,
  Acknowledged = 2,
  Deleted = 3,
}

// Notice action types
export interface NoticeAction {
  label: string;
  actionType: string;
  actionData: string;
}

// Friend Request Notice Data
export interface FriendRequestData {
  connectionRequestId?: string;
  requesterId?: string;
  requesterName?: string;
  requesterAvatarUrl?: string;
  requesterDescription?: string;
  receiverUserId?: string;
}

// Group Invitation Notice Data
export interface GroupInvitationData {
  groupId: string;
  groupName: string;
  invitedBy: string;
}

// Adventure Invitation Notice Data
export interface AdventureInvitationData {
  adventureId: string;
  adventureName: string;
  invitedBy: string;
}

// Adventure Opened Notice Data
// PascalCase because the server serializes an anonymous object as-is — same as
// SessionInviteNoticeData below. Don't "fix" the casing without changing the sender.
export interface AdventureOpenedData {
  AdventureId: string;
  AdventureName: string;
}

// Group Join Request Notice Data
export interface GroupJoinRequestData {
  type: 'GroupJoinRequest';
  relatedId: string; // Group ID
  requesterId?: string; // User who requested to join (from notice.userId)
  requesterName?: string; // Resolved user name
  requesterAvatarUrl?: string; // Resolved user avatar
}

// Session Invite Notice Data
export interface SessionInviteNoticeData {
  SessionId: string;
  SessionName: string;
  AdventureId: string;
  AdventureName: string;
  InvitedBy: string;
  InviterName: string;
}

// Discussion Reply Notice Data
// PascalCase: the server serializes an anonymous object as-is, same as AdventureOpenedData.
export interface DiscussionReplyData {
  DiscussionId: string;
  DiscussionTitle: string;
  /** The reply that raised the notice. Stale once the notice collapses further replies into it. */
  PostId: string;
}

// Moderator Approval Request Notice Data
export interface ModeratorApprovalRequestData {
  sessionId: string
  adventureId: string
  nodeId: string
  nodeLabel?: string
  requestingUsers: string[]
}

export interface UserNotice {
  id: string;
  created: string; // Backend uses 'created' instead of 'timestamp'
  status: NoticeStatus; // Backend uses status enum instead of boolean flags
  type: string; // Backend uses GUID string for type
  quickMessage: string; // Backend uses 'quickMessage' instead of 'message'
  title: string;
  userId: string;
  expirationDate: string | null;
  iconUrl: string | null;
  actionsJson: string | null;
  data: string | null; // JSON string containing additional data

  // Computed properties for compatibility with UI
  timestamp?: string;
  message?: string;
  read?: boolean;
  acknowledged?: boolean;
  actions?: NoticeAction[];
  parsedData?: FriendRequestData | GroupInvitationData | AdventureInvitationData | AdventureOpenedData | SessionInviteNoticeData | ModeratorApprovalRequestData | DiscussionReplyData | any;
  priority?: NoticePriority;
}

export interface CreateUserNoticeRequest {
  type: string; // GUID of notice type
  title: string;
  quickMessage: string;
  data?: string; // JSON string
  iconUrl?: string;
  expirationDate?: string;
}

export interface UnreadCountResponse {
  count: number;
}

// ============================================================================
// Group formation and scheduling notice data
// ============================================================================

/** Data for {@link NoticeTypeIds.SESSION_MATCH_AVAILABLE}. */
export interface SessionMatchAvailableData {
  adventureId: string;
  sessionId: string;
  /** When the group means to go, if it has settled on a time yet. */
  scheduledStartDate?: string | null;
}

/** Data for {@link NoticeTypeIds.SESSION_SCHEDULE_DECIDED}. */
export interface SessionScheduleDecidedData {
  sessionId: string;
  adventureId: string;
  /** Absent when the poll closed without agreeing anything. */
  scheduledStartDate?: string | null;
}

/** Data for {@link NoticeTypeIds.SESSION_SPLIT_PROPOSED}. */
export interface SessionSplitProposedData {
  /** What Accept and Decline act on — not a session id; the session does not exist yet. */
  proposalId: string;
  originSessionId: string;
  adventureId: string;
  proposedStartUtc: string;
}
